import { setupChannel } from '../user_socket';
import Experience from '../../src/Experience/Experience';
import SandboxHelp from '../../src/Experience/Systems/SandboxHelp';

const ExperienceHook = {
    mounted() {
        const channelId = this.el.dataset.channelId;
        const deckJson = this.el.dataset.deck;
        const zonesJson = this.el.dataset.zonesLayout;
        const settingsJson = this.el.dataset.experienceSettings;
        const focusedCardIdFromDom = this.el.dataset.focusedCardId;
        const liveViewSync = this.el.dataset.liveViewSync !== 'false';
        const consumeEditorSync = this.el.dataset.consumeEditorSync === 'true';

        if (!channelId) {
            console.error('ExperienceHook: missing data-channel-id');
            return;
        }

        const deck = deckJson ? JSON.parse(deckJson) : null;
        const zonesLayout = zonesJson ? JSON.parse(zonesJson) : null;
        const experienceSettings = settingsJson ? JSON.parse(settingsJson) : {};

        const { channel, presence } = setupChannel(channelId, {});
        this.channel = channel;
        this._liveViewSync = liveViewSync;

        // A dropped socket means broadcasts were missed, so state must be
        // re-fetched once the tab is usable again.
        channel.onError(() => {
            this._needsRefresh = true;
        });

        channel.join()
            .receive('ok', ({ user_id, instance_id, card_positions, focused_card_id, experience_state, seq }) => {
                this._expectedSeq = seq + 1;
                this._refreshing = false;

                const mergedExperienceSettings = {
                    ...experienceSettings,
                    experience_state: experience_state || experienceSettings.experience_state || {},
                };

                this.experience = new Experience(this.el, {
                    channel,
                    presence,
                    user_id,
                    instance_id,
                    card_positions,
                    deck,
                    zones_layout: zonesLayout,
                    experience_settings: mergedExperienceSettings,
                    experience_state: experience_state || {}
                });
                this.experience.loadShaders();
                this.sandboxHelp = new SandboxHelp(this.experience.eventBus);

                // Clean up cursors when users leave
                presence.onLeave((id, current, leftPres) => {
                    if (this.experience && this.experience.remoteCursors) {
                        this.experience.remoteCursors.removeCursor(id);
                    }
                });

                // Restore persisted focus once cards are loaded.
                const initialFocusedCardId = focused_card_id || focusedCardIdFromDom;
                if (initialFocusedCardId) {
                    this.experience.eventBus.once('resourcesLoaded', () => {
                        try {
                            this.experience.space.focusCard(initialFocusedCardId);
                        } catch (e) {
                            console.warn('initial focus_card: card not found', initialFocusedCardId);
                        }
                    });
                }

                if (liveViewSync) {
                    // Wire card focus/defocus events back to LiveView
                    this._wireCardEventsToLiveView();

                    // Persist camera position after 2D pan
                    this.experience.eventBus.on('camera_panned', (event) => {
                        this.pushEvent('update_camera_pan', event.detail);
                    });
                }
            })
            .receive('error', (resp) => {
                console.error('ExperienceHook: unable to join channel', resp);
            });

        // LiveView tells us to switch board → push to channel
        this.handleEvent('switch_board', ({ board_id }) => {
            channel.push('switchBoard', { board_id });
        });

        // Channel broadcasts board switch (from any client) → update LiveView + reset experience
        channel.on('boardSwitched', ({ board_id, card_positions, seq }) => {
            this._updateSeq(seq);
            // Tell LiveView so it updates the dropdown and its assigns
            this.pushEvent('board_switched', { board_id });

            // Fetch the YAML deck from LiveView, then reset with positions applied
            this._pendingCardPositions = card_positions;
        });

        // Channel broadcasts card arrangement (from any client)
        channel.on('cardsArranged', ({ cards }) => {
            if (this.experience) {
                this.experience.handleCardsArranged(cards);
            }
        });

        channel.on('experienceStateUpdated', ({ state }) => {
            if (this.experience) {
                this.experience.applyBehaviorState(state || {});
            }
        });

        if (consumeEditorSync) {
            channel.on('settingsUpdated', ({ settings }) => {
                if (this.experience) {
                    this.experience.applySettings(settings);
                }
            });

            channel.on('zoneLayoutUpdated', ({ zones_layout }) => {
                if (this.experience) {
                    this.experience.resetZones(zones_layout);
                }
            });

            channel.on('cardAdded', (cardData) => {
                if (this.experience) {
                    this.experience.addCardFromServer(cardData);
                }
            });

            channel.on('cardTextureUpdated', ({ card_id, texture_url, side }) => {
                if (this.experience) {
                    this.experience.replaceCardTexture(card_id, texture_url, side);
                }
            });

            channel.on('experience_switched', ({ deck, settings, zones_layout }) => {
                if (this.experience) {
                    if (zones_layout !== undefined) {
                        this.experience.zonesLayout = zones_layout;
                    }
                    this.experience.reset(deck, settings);
                }
            });
        }

        // LiveView responds with the full deck data for the reset
        this.handleEvent('board_updated', (payload) => {
            if (this.experience) {
                const positions = this._pendingCardPositions || {};
                this._pendingCardPositions = null;
                const deck = this.experience.applyCardPositions(payload.deck, positions);

                if (payload.zones_layout !== undefined) {
                    this.experience.zonesLayout = payload.zones_layout;
                }
                this.experience.reset(deck, payload.settings || null);
            }
        });

        this.handleEvent('zones_updated', (payload) => {
            if (this.experience) {
                this.experience.resetZones(payload.zones_layout);
            }
        });

        this.handleEvent('settings_updated', ({ settings }) => {
            if (this.experience) {
                this.experience.applySettings(settings);
            }
        });

        // Card management events from LiveView
        this.handleEvent('focus_card', ({ card_id }) => {
            if (this.experience && this.experience.space) {
                try {
                    this.experience.space.focusCard(card_id);
                } catch (e) {
                    console.warn('focus_card: card not found', card_id);
                }
            }
        });

        this.handleEvent('defocus_card', () => {
            if (this.experience && this.experience.space) {
                this.experience.space.defocusAnyCard();
            }
        });

        this.handleEvent('flip_card', ({ card_id, face_up }) => {
            if (this.experience && this.experience.space) {
                try {
                    const card = this.experience.space.getCardById(card_id);
                    if (!!face_up !== !!card.isFaceUp) {
                        this.experience.space.flipCard(card_id);
                    }
                } catch (e) {
                    console.warn('flip_card: card not found', card_id);
                }
            }
        });

        this.handleEvent('card_added', (cardData) => {
            if (this.experience) {
                this.experience.addCardFromServer(cardData);
            }
        });

        this.handleEvent('card_texture_updated', ({ card_id, texture_url, side }) => {
            if (this.experience) {
                this.experience.replaceCardTexture(card_id, texture_url, side);
            }
        });

        this.handleEvent('asset_changed', ({ type, path }) => {
            if (this.experience) {
                this.experience.handleAssetChanged(type, path);
            }
        });

        this.handleEvent('cards_arranged', ({ cards }) => {
            if (this.experience) {
                this.experience.handleCardsArranged(cards);
            }
        });

        // Visibility change: only resync when the connection dropped while hidden
        this._onVisibilityChange = () => {
            if (document.visibilityState !== 'visible') return;
            if (!this._needsRefresh) return;

            this._requestRefresh('reconnected while hidden');
        };
        document.addEventListener('visibilitychange', this._onVisibilityChange);
    },

    _wireCardEventsToLiveView() {
        if (!this.experience) return;
        const eventBus = this.experience.eventBus;

        eventBus.on('focusedCard', (event) => {
            this.pushEvent('card_focused', { id: event.detail.id });
        });

        eventBus.on('defocusedCard', (event) => {
            this.pushEvent('card_defocused', { id: event.detail.id });
        });

        eventBus.on('flippedCardUp', (event) => {
            this.pushEvent('card_flipped', { id: event.detail.id, face_up: true });
        });

        eventBus.on('flippedCardDown', (event) => {
            this.pushEvent('card_flipped', { id: event.detail.id, face_up: false });
        });
    },

    _updateSeq(seq) {
        if (seq === undefined || seq === null) return;
        if (seq > this._expectedSeq) {
            console.warn(`[ExperienceHook] seq gap: expected ${this._expectedSeq}, got ${seq}`);
            this._requestRefresh('seq gap detected');
        }
        this._expectedSeq = seq + 1;
    },

    _requestRefresh(reason) {
        if (this._refreshing || !this.channel) return;
        this._refreshing = true;
        console.log(`[ExperienceHook] requesting refresh: ${reason}`);

        this.channel.push('refresh', {})
            .receive('ok', ({ card_positions, focused_card_id, experience_state, seq }) => {
                this._expectedSeq = seq + 1;
                this._refreshing = false;
                this._needsRefresh = false;
                console.log(`[ExperienceHook] refreshed state from server, seq=${seq}`);

                if (this.experience) {
                    // Applying positions in place keeps textures and shaders loaded;
                    // a reset would rebuild the whole scene and flash the cards.
                    if (!this.experience.syncCardPositions(card_positions)) {
                        const currentDeck = this.experience.deckLoader?.deck;
                        if (currentDeck) {
                            const merged = this.experience.applyCardPositions(currentDeck, card_positions);
                            this.experience.reset(merged);
                        }
                    }
                    this.experience.applyBehaviorState(experience_state || {});
                }
                if (this._liveViewSync) {
                    this.pushEvent('experience_state_updated', { state: experience_state || {} });
                }
            })
            .receive('error', () => {
                this._refreshing = false;
                console.error('[ExperienceHook] refresh failed');
            });
    },

    destroyed() {
        if (this._onVisibilityChange) {
            document.removeEventListener('visibilitychange', this._onVisibilityChange);
            this._onVisibilityChange = null;
        }
        if (this.sandboxHelp) {
            this.sandboxHelp.destroy();
            this.sandboxHelp = null;
        }
        if (this.experience) {
            this.experience.destroy();
            this.experience = null;
        }
        if (this.channel) {
            this.channel.leave();
            this.channel = null;
        }
    }
};

export default ExperienceHook;
